import React, { useEffect, useMemo, useRef, useState } from "react";
import { ClickAwayListener, InputAdornment, Paper } from "@mui/material";
import { Search } from "@mui/icons-material";
import AppTextField from "../../../lib/components/AppTextField";
import { plansGrantingRule } from "../utils";

/**
 * Searchable capability (rule) picker matching the marketplace create-item mockup.
 */
const CapabilityRulePicker = ({
  rules = [],
  value = null,
  onChange,
  disabled = false,
  error,
  helperText,
}) => {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState("");
  const inputRef = useRef(null);

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return rules;
    return rules.filter(
      (r) =>
        String(r.label || "").toLowerCase().includes(q) ||
        String(r.rule || "").toLowerCase().includes(q) ||
        String(r.hint || "").toLowerCase().includes(q)
    );
  }, [rules, query]);

  const selected = useMemo(
    () => (value ? rules.find((r) => r.rule === value) : null),
    [rules, value]
  );

  useEffect(() => {
    if (selected) setQuery("");
  }, [selected]);

  return (
    <ClickAwayListener onClickAway={() => setOpen(false)}>
      <div className="mp-combobox">
        {!selected ? (
          <>
            <AppTextField
              inputRef={inputRef}
              placeholder="Search capabilities…"
              value={query}
              disabled={disabled}
              onChange={(e) => {
                setQuery(e.target.value);
                setOpen(true);
              }}
              onFocus={() => setOpen(true)}
              error={!!error}
              helperText={helperText}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <Search fontSize="small" className="text-secondary" />
                  </InputAdornment>
                ),
              }}
            />
            {open ? (
              <Paper className="mp-rule-menu" elevation={3}>
                {filtered.length ? (
                  filtered.map((r) => {
                    const granted = plansGrantingRule(r);
                    return (
                      <button
                        type="button"
                        key={r.rule}
                        className="mp-rule-opt"
                        onClick={() => {
                          onChange?.(r);
                          setOpen(false);
                          setQuery("");
                        }}
                      >
                        <div className="rname">{r.label}</div>
                        <div className="rhint">{r.hint || r.rule}</div>
                        <div className="plan-badges">
                          {(r.plans || []).map((p) =>
                            p.granted ? (
                              <span key={p.id || p.name} className="plan-badge">
                                {p.name}: {p.unlimited ? "∞" : p.value ?? "included"}
                              </span>
                            ) : (
                              <span
                                key={p.id || p.name}
                                className="plan-badge zero"
                              >
                                {p.name}: not included
                              </span>
                            )
                          )}
                          {!granted.length && !(r.plans || []).length ? (
                            <span className="plan-badge zero">No plan data</span>
                          ) : null}
                        </div>
                      </button>
                    );
                  })
                ) : (
                  <div className="mp-rule-opt">
                    <span className="rhint">No matching capability</span>
                  </div>
                )}
              </Paper>
            ) : null}
          </>
        ) : (
          <div className="mp-selected-rule">
            <div className="txt">
              <div className="t1">{selected.label}</div>
              <div className="t2 mono">
                {selected.rule} · {selected.type}
              </div>
            </div>
            {!disabled ? (
              <button
                type="button"
                className="mp-link-btn"
                onClick={() => {
                  onChange?.(null);
                  setTimeout(() => inputRef.current?.focus(), 0);
                }}
              >
                Change
              </button>
            ) : null}
          </div>
        )}

        {selected?.soldBy?.length ? (
          <div className="mp-callout warn">
            <span className="dot" />
            <span>Already sold by {selected.soldBy.join(", ")}</span>
          </div>
        ) : null}
      </div>
    </ClickAwayListener>
  );
};

export default CapabilityRulePicker;
